// services/dashboard/formatFactorsData.js

/**
 * ⚙️ Extrae y procesa todos los factores del JSON
 * @param {Object} jsonData - Datos crudos de la empresa
 * @returns {Map} Mapa con los factores consolidados
 */
const extractFactors = (jsonData) => {
    const factorMap = new Map();
    const seenCompanyFactors = new Set(); // 🔍 Para rastrear factores ya procesados por empresa

    // 🔍 Determinar la raíz de los datos (Soporte para estructura con o sin wrapper 'data')
    const rootData = jsonData.data || jsonData;

    //console.log('📦 Procesando datos para factors, root keys:', Object.keys(rootData));

    // Recorrer todas las fechas
    for (const date in rootData) {
        const companies = rootData[date];

        // Validación extra para evitar errores
        if (!companies || typeof companies !== 'object') continue;

        // Recorrer todas las empresas
        for (const companyKey in companies) {
            const company = companies[companyKey];

            // Recorrer todos los grupos (excepto company_type)
            for (const groupKey in company) {
                if (groupKey === 'company_type') continue;

                const group = company[groupKey];

                // Recorrer todos los indicadores dentro del grupo
                for (const indicatorKey in group) {
                    const indicator = group[indicatorKey];

                    // Verificar si existe el objeto 'factor' dentro del indicador
                    if (indicator.factor && typeof indicator.factor === 'object') {
                        const factor = indicator.factor;

                        // 🚨 VALIDACIÓN: Excluir factores con name null y factor_added null
                        if (factor.name == null && factor.factor_added == null) {
                            //console.log(`⛔ Ignorando factor sin nombre ni factor_added en indicador '${indicatorKey}' de empresa '${companyKey}'`);
                            continue;
                        }

                        // Obtener nombre, valor y unidad del factor
                        const factorName = factor.name || 'Factor sin nombre';
                        const factorValue = typeof factor.value === 'number' ? factor.value : 0;
                        const factorUnit = factor.unit || 'CO2e';
                        const indicatorColor = indicator.indicator_color || '#097F9F';

                        // 🔍 Validar duplicados por empresa
                        const uniqueKey = `${companyKey}-${factorName}`;
                        if (seenCompanyFactors.has(uniqueKey)) {
                            continue;
                        }
                        seenCompanyFactors.add(uniqueKey);

                        //console.log(`🔍 Factor encontrado: ${factorName} = ${factorValue} ${factorUnit} (${companyKey})`);

                        // Consolidar factores con el mismo nombre para el gráfico global
                        if (factorMap.has(factorName)) {
                            const existing = factorMap.get(factorName);
                            existing.totalValue += factorValue;
                            existing.count += 1;
                        } else {
                            factorMap.set(factorName, {
                                indicatorName: factorName,
                                totalValue: factorValue,
                                unit: factorUnit,
                                color: indicatorColor,
                                count: 1
                            });
                        }
                    }
                }
            }
        }
    }

    //console.log(`📊 Total de factores únicos encontrados: ${factorMap.size}`);
    return factorMap;
};

/**
 * 📊 Formatea los factors extraídos para Chart.js (Múltiples datasets)
 */
export const formatFactorsData = (jsonData) => {
    const factorMap = extractFactors(jsonData);
    const sortedFactors = Array.from(factorMap.values())
        .sort((a, b) => b.totalValue - a.totalValue);

    if (sortedFactors.length === 0) {
        console.warn('⚠️ No se encontraron factors en los datos');
        return { labels: [], datasets: [] };
    }

    // Crear un dataset por cada factor
    const datasets = sortedFactors.map((factor) => ({
        label: factor.indicatorName,
        data: [Number(factor.totalValue.toFixed(2))],
        backgroundColor: factor.color,
        borderColor: factor.color,
        borderWidth: 1,
        unit: factor.unit
    }));

    //console.log('✅ Factors procesados exitosamente:', {
    //     totalFactores: sortedFactors.length,
    //     topFactores: sortedFactors.slice(0, 5).map(f => ({
    //         nombre: f.indicatorName,
    //         valorTotal: f.totalValue.toFixed(2),
    //         unidad: f.unit,
    //         cantidadReportes: f.count
    //     }))
    // });

    return {
        labels: ['Valores'],
        datasets
    };
};

/**
 * 🎨 Versión dataset único (todos los factores en una sola barra)
 */
export const formatFactorsDataSingleDataset = (jsonData) => {
    const factorMap = extractFactors(jsonData);
    const sortedFactors = Array.from(factorMap.values())
        .sort((a, b) => b.totalValue - a.totalValue);

    if (sortedFactors.length === 0) {
        return { labels: [], datasets: [] };
    }

    return {
        labels: sortedFactors.map(f => f.indicatorName),
        datasets: [{
            label: 'Factores de indicadores',
            data: sortedFactors.map(f => Number(f.totalValue.toFixed(2))),
            backgroundColor: sortedFactors.map(f => f.color),
            borderColor: sortedFactors.map(f => f.color),
            borderWidth: 1,
            unit: 'CO2e'
        }]
    };
};